import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Button,
  Chip,
  Divider,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Avatar,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControlLabel,
  Switch,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  ListItemButton,
  Tooltip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  InputAdornment,
  Tabs,
  Tab
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { format, addDays } from 'date-fns';
import { event as eventApi, user as userApi, car as carApi, task as taskApi, racetrack as racetrackApi } from '../../utils/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../components/auth/AuthModule';
import ImageUploader from '../../components/shared/ImageUploader';
import { getTransformedImageUrl } from '../../utils/filestack';
import {
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  DirectionsCar as TravelIcon,
  Hotel as HotelIcon,
  Add as AddIcon
} from '@mui/icons-material';

// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PeopleIcon from '@mui/icons-material/People';
import EuroIcon from '@mui/icons-material/Euro';
import LinkIcon from '@mui/icons-material/Link';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ClearIcon from '@mui/icons-material/Clear';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import FlagIcon from '@mui/icons-material/Flag';
import LockIcon from '@mui/icons-material/Lock';
import InfoIcon from '@mui/icons-material/Info';

// Add a helper function to render user avatars
const UserAvatar = ({ user, size = 'medium' }) => {
  const [imageError, setImageError] = useState(false);
  
  // Default sizes
  const sizes = {
    small: { width: 32, height: 32 },
    medium: { width: 40, height: 40 },
    large: { width: 56, height: 56 }
  };
  
  const sizeProps = sizes[size] || sizes.medium;
  
  // Get a color based on the user's role
  const getRoleColor = (role) => {
    switch (role) {
      case 'mechanic':
        return 'info';
      case 'management':
        return 'secondary';
      case 'admin':
        return 'error';
      case 'driver':
      default:
        return 'primary';
    }
  };
  
  const bgColor = user?.role ? getRoleColor(user.role) : 'primary';
  
  // Check if the imageUrl is valid and not empty
  const hasValidImage = !imageError && user?.imageUrl && user.imageUrl.trim() !== '';
  
  // Construct the full image URL if it's a relative path
  let imageUrl = '';
  if (hasValidImage) {
    // If the URL doesn't start with http or https, assume it's a relative path
    if (!user.imageUrl.startsWith('http://') && !user.imageUrl.startsWith('https://')) {
      // Construct the full URL based on your API base URL
      const apiBaseUrl = process.env.REACT_APP_API_URL || '';
      imageUrl = `${apiBaseUrl}${user.imageUrl.startsWith('/') ? '' : '/'}${user.imageUrl}`;
    } else {
      imageUrl = user.imageUrl;
    }
  }
  
  return (
    <Avatar 
      src={imageUrl}
      onError={(e) => {
        console.error(`Error loading image for ${user?.name || user?.username}:`, e);
        setImageError(true);
      }}
      alt={user?.name || user?.username || 'User'} 
      sx={{ 
        ...sizeProps,
        bgcolor: `${bgColor}.main`
      }}
    >
      {(user?.name || user?.username || '?').charAt(0).toUpperCase()}
    </Avatar>
  );
};

const EventDetailPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('events');
  const { user } = useAuth();
  
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attendanceStatus, setAttendanceStatus] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    location: '',
    date: '',
    endDate: '',
    price: 0,
    signupLink: '',
    maxParticipants: '',
    isPublic: true,
    isRaceday: false,
    racetrack: null,
    imageUrl: ''
  });
  const [saving, setSaving] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [hasPaid, setHasPaid] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [participantCars, setParticipantCars] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);
  const [loadingCars, setLoadingCars] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [taskCreated, setTaskCreated] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [userCars, setUserCars] = useState([]);
  const [selectedEventCar, setSelectedEventCar] = useState(null);
  const [loadingUserCars, setLoadingUserCars] = useState(false);
  const [carSelectOpen, setCarSelectOpen] = useState(false);
  const [adminCarSelectOpen, setAdminCarSelectOpen] = useState(false);
  const [selectedParticipantForCar, setSelectedParticipantForCar] = useState(null);
  const [participantCarsList, setParticipantCarsList] = useState([]);
  const [loadingParticipantCars, setLoadingParticipantCars] = useState(false);
  const [updatingParticipantCar, setUpdatingParticipantCar] = useState(false);
  const [assignedUser, setAssignedUser] = useState(null);
  const [daysUntilDue, setDaysUntilDue] = useState(15);
  const [availableRacetracks, setAvailableRacetracks] = useState([]);
  const [loadingRacetracks, setLoadingRacetracks] = useState(false);
  const [selectedRacetrack, setSelectedRacetrack] = useState(null);
  const [addParticipantDialogOpen, setAddParticipantDialogOpen] = useState(false);
  const [addingParticipant, setAddingParticipant] = useState(false);
  const [raceFinishedDialogOpen, setRaceFinishedDialogOpen] = useState(false);
  const [participantLaps, setParticipantLaps] = useState({});
  const [savingRaceResults, setSavingRaceResults] = useState(false);
  const [participantLapDialogOpen, setParticipantLapDialogOpen] = useState(false);
  const [userLapCount, setUserLapCount] = useState(0);
  const [savingUserLaps, setSavingUserLaps] = useState(false);
  const [travelCostsDialogOpen, setTravelCostsDialogOpen] = useState(false);
  const [payTravelDebtDialogOpen, setPayTravelDebtDialogOpen] = useState(false);
  const [selectedParticipantForDebt, setSelectedParticipantForDebt] = useState(null);
  const [editEvent, setEditEvent] = useState({
    title: '',
    description: '',
    location: '',
    date: null,
    endDate: null,
    price: 0,
    isRaceday: false,
    racetrack: null,
    signupLink: '',
    isPublic: true,
    imageUrl: '',
    hasTravelCosts: false,
    travelCostPerPerson: 0,
    travelCostsPaidBy: '',
    travelCostsNotes: '',
  });
  const [activeTab, setActiveTab] = useState('information');

  useEffect(() => {
    fetchEventDetails();
  }, [eventId]);

  useEffect(() => {
    if (inviteDialogOpen) {
      fetchAvailableUsers();
    }
  }, [inviteDialogOpen]);

  useEffect(() => {
    if (addParticipantDialogOpen) {
      fetchAllUsers();
    }
  }, [addParticipantDialogOpen]);

  useEffect(() => {
    if (showTaskForm && allUsers.length === 0) {
      fetchAllUsers();
    }
  }, [showTaskForm, allUsers.length]);

  useEffect(() => {
    if (isEditMode) {
      fetchRacetracks();
    }
  }, [isEditMode]);

  useEffect(() => {
    if (event && event.racetrack) {
      setSelectedRacetrack(event.racetrack);
    }
  }, [event]);

  // Update location when a racetrack is selected in edit mode
  useEffect(() => {
    if (isEditMode && selectedRacetrack && editFormData.isRaceday) {
      setEditFormData(prev => ({
        ...prev,
        location: `${selectedRacetrack.name}, ${selectedRacetrack.location}, ${selectedRacetrack.country}`
      }));
    }
  }, [selectedRacetrack, editFormData.isRaceday, isEditMode]);

  // Reset location field when isRaceday is toggled off
  useEffect(() => {
    if (isEditMode && !editFormData.isRaceday && event) {
      setEditFormData(prev => ({
        ...prev,
        location: event.location
      }));
    }
  }, [editFormData.isRaceday, isEditMode, event]);

  // Fetch user's cars when component mounts
  useEffect(() => {
    if (user) {
      fetchUserCars();
    }
  }, [user]);

  // Fetch event details when component mounts
  useEffect(() => {
    if (eventId) {
      fetchEventDetails();
    }
  }, [eventId]);

  // Fetch user's cars
  const fetchUserCars = async () => {
    if (!user) return;
    
    try {
      setLoadingUserCars(true);
      const cars = await carApi.getUserCars();
      setUserCars(cars);
      
      // If the user is already a participant with a car selected, set it as the selected car
      if (event && event.participants) {
        const userParticipation = event.participants.find(p => p.user._id === user._id);
        if (userParticipation && userParticipation.car) {
          setSelectedEventCar(userParticipation.car);
        }
      }
    } catch (error) {
      console.error('Error fetching user cars:', error);
      toast.error('Failed to load your cars');
    } finally {
      setLoadingUserCars(false);
    }
  };

  const fetchEventDetails = async () => {
    try {
      setLoading(true);
      const data = await eventApi.getById(eventId);
      
      // Debug log to see what data is being returned
      console.log('Event details:', {
        eventId,
        createdBy: data.createdBy,
        hasCreatedByImageUrl: data.createdBy && !!data.createdBy.imageUrl,
        createdByImageUrl: data.createdBy?.imageUrl,
        participants: data.participants.map(p => ({
          userId: p.user._id,
          userName: p.user.name,
          hasUserImageUrl: !!p.user.imageUrl,
          userImageUrl: p.user.imageUrl
        })),
        // Add travel costs debug info
        hasTravelCosts: data.hasTravelCosts,
        travelCostPerPerson: data.travelCostPerPerson,
        travelCostsPaidBy: data.travelCostsPaidBy,
        travelCostsPaidByName: data.travelCostsPaidBy ? data.travelCostsPaidBy.name : null
      });
      
      setEvent(data);
      
      // Initialize edit form data
      setEditFormData({
        title: data.title || '',
        description: data.description || '',
        location: data.location || '',
        date: data.date ? format(new Date(data.date), 'yyyy-MM-dd') : '',
        endDate: data.endDate ? format(new Date(data.endDate), 'yyyy-MM-dd') : '',
        price: data.price || 0,
        signupLink: data.signupLink || '',
        maxParticipants: data.maxParticipants || '',
        isPublic: data.isPublic !== undefined ? data.isPublic : true,
        isRaceday: data.isRaceday || false,
        racetrack: data.racetrack ? data.racetrack._id : null,
        imageUrl: data.imageUrl || ''
      });
      
      // Set current user's attendance status
      if (user && data.participants) {
        const userParticipation = data.participants.find(p => p.user._id === user._id);
        if (userParticipation) {
          setAttendanceStatus(userParticipation.status);
          setHasPaid(userParticipation.hasPaid || false);
          
          // Set selected car if user has one for this event
          if (userParticipation.car) {
            setSelectedEventCar(userParticipation.car);
          }
        } else {
          setAttendanceStatus(null);
          setHasPaid(false);
          setSelectedEventCar(null);
        }
      }

      // Set invited users for private events
      if (data.invitedUsers) {
        setInvitedUsers(data.invitedUsers);
      }

      // Update editEvent with travel costs data
      setEditEvent({
        title: data.title,
        description: data.description,
        location: data.location,
        date: data.date ? new Date(data.date) : null,
        endDate: data.endDate ? new Date(data.endDate) : null,
        price: data.price || 0,
        isRaceday: data.isRaceday || false,
        racetrack: data.racetrack || null,
        signupLink: data.signupLink || '',
        isPublic: data.isPublic !== undefined ? data.isPublic : true,
        imageUrl: data.imageUrl || '',
        hasTravelCosts: data.hasTravelCosts || false,
        travelCostPerPerson: data.travelCostPerPerson || 0,
        travelCostsPaidBy: data.travelCostsPaidBy ? data.travelCostsPaidBy._id : '',
        travelCostsNotes: data.travelCostsNotes || '',
      });
    } catch (error) {
      console.error('Error fetching event details:', error);
      toast.error(t('errors.loadingEventDetails'));
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      setLoadingUsers(true);
      const users = await userApi.getUsers();
      
      // Filter out users who are already participants
      const participantIds = event.participants?.map(p => p.user._id) || [];
      
      const filteredUsers = users.filter(u => 
        !participantIds.includes(u._id) && 
        u._id !== user._id
      );
      
      setAvailableUsers(filteredUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(t('errors.loadingUsers'));
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      setLoadingUsers(true);
      const users = await userApi.getUsers();
      console.log('Fetched users:', users);
      
      // Sort users by role, prioritizing mechanics
      const sortedUsers = users.sort((a, b) => {
        if (a.role === 'mechanic' && b.role !== 'mechanic') return -1;
        if (a.role !== 'mechanic' && b.role === 'mechanic') return 1;
        return a.name.localeCompare(b.name);
      });
      
      setAllUsers(sortedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Error fetching users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchRacetracks = async () => {
    try {
      setLoadingRacetracks(true);
      const racetracks = await racetrackApi.getAll();
      setAvailableRacetracks(racetracks);
    } catch (error) {
      console.error('Error fetching racetracks:', error);
      toast.error('Error loading racetracks');
    } finally {
      setLoadingRacetracks(false);
    }
  };

  const handleAttendanceChange = async (event, newStatus) => {
    if (newStatus === null) return;
    
    try {
      setUpdatingStatus(true);
      
      // Check if user is already a participant
      const isParticipant = attendanceStatus !== null;
      
      if (isParticipant) {
        // Update existing participation
        await eventApi.updateParticipantStatus(eventId, { 
          status: newStatus,
          carId: selectedEventCar ? selectedEventCar._id : null
        });
        toast.success(t('success.statusUpdated'));
      } else {
        // Add user as a new participant
        await eventApi.addParticipant(eventId, { 
          status: newStatus,
          carId: selectedEventCar ? selectedEventCar._id : null
        });
        toast.success(t('success.joinedEvent'));
      }
      
      fetchEventDetails();
    } catch (error) {
      console.error('Error updating attendance:', error);
      toast.error(t('errors.updatingStatus'));
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handlePaymentStatusChange = async () => {
    if (!attendanceStatus) return;
    
    try {
      setUpdatingStatus(true);
      const newPaymentStatus = !hasPaid;
      
      await eventApi.updateParticipantStatus(eventId, { 
        status: attendanceStatus,
        hasPaid: newPaymentStatus 
      });
      
      setHasPaid(newPaymentStatus);
      toast.success(newPaymentStatus 
        ? t('success.paymentMarkedAsPaid') 
        : t('success.paymentMarkedAsUnpaid'));
      
      fetchEventDetails();
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast.error(t('errors.updatingPaymentStatus'));
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditEvent({
      ...editEvent,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleRacetrackChange = (event, newValue) => {
    setSelectedRacetrack(newValue);
    if (newValue) {
      setEditEvent(prev => ({
        ...prev,
        racetrack: newValue._id
      }));
    } else {
      setEditEvent(prev => ({
        ...prev,
        racetrack: null
      }));
    }
  };

  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      
      // Format the data
      const eventData = {
        ...editEvent,
        price: editEvent.price ? Number(editEvent.price) : 0,
        maxParticipants: editEvent.maxParticipants ? Number(editEvent.maxParticipants) : null
      };
      
      // Add racetrack if selected and isRaceday is true
      if (eventData.isRaceday && selectedRacetrack) {
        eventData.racetrack = selectedRacetrack._id;
      } else if (!eventData.isRaceday) {
        eventData.racetrack = null;
      }
      
      await eventApi.update(eventId, eventData);
      
      toast.success(t('success.eventUpdated'));
      setIsEditMode(false);
      fetchEventDetails();
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error(t('errors.updatingEvent'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvent = async () => {
    try {
      await eventApi.delete(eventId);
      toast.success(t('success.eventDeleted'));
      navigate('/events');
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error(t('errors.deletingEvent'));
    }
  };

  const handleInviteUser = async () => {
    if (!selectedUser) return;
    
    try {
      setInviting(true);
      
      // For private events, add to invited users list
      if (!event.isPublic) {
        await eventApi.inviteUser(eventId, { userId: selectedUser._id });
      }
      
      // Add them as a participant with "maybe" status
      await eventApi.addSpecificParticipant(eventId, { 
        userId: selectedUser._id, 
        status: 'maybe',
        hasPaid: false
      });
      
      toast.success(t('success.userInvitedAndAdded', { user: selectedUser.name }));
      setSelectedUser(null);
      setInviteDialogOpen(false);
      fetchEventDetails();
      fetchAvailableUsers();
    } catch (error) {
      console.error('Error inviting user:', error);
      toast.error(t('errors.invitingUser'));
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveParticipant = async (userId) => {
    try {
      // Check if the event is private (has invited users)
      const isPrivateEvent = !event.isPublic;
      const isInvited = isPrivateEvent && event.invitedUsers && event.invitedUsers.some(u => u._id === userId);
      
      // Always remove from participants list
      await eventApi.removeSpecificParticipant(eventId, userId);
      
      // If it's a private event and the user was invited, also remove from invited users
      if (isPrivateEvent && isInvited) {
        await eventApi.removeInvitedUser(eventId, userId);
        toast.success(t('success.userRemovedFromEvent'));
      } else {
        toast.success('Participant removed successfully');
      }
      
      fetchEventDetails();
      fetchAvailableUsers();
    } catch (error) {
      console.error('Error removing participant:', error);
      toast.error('Error removing participant');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'attending':
        return 'success';
      case 'not_attending':
        return 'error';
      case 'maybe':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleDaysUntilDueChange = (event) => {
    const value = parseInt(event.target.value);
    if (!isNaN(value) && value > 0) {
      setDaysUntilDue(value);
    }
  };

  const handleToggleTaskForm = () => {
    const newState = !showTaskForm;
    setShowTaskForm(newState);
    
    // Reset form state when toggling off
    if (!newState) {
      setSelectedParticipant(null);
      setParticipantCars([]);
      setSelectedCar(null);
      setAssignedUser(null);
      setDaysUntilDue(15);
      setTaskCreated(false);
    }
  };

  const handleParticipantChange = async (event) => {
    const participantId = event.target.value;
    if (!participantId) {
      setSelectedParticipant(null);
      setParticipantCars([]);
      setSelectedCar(null);
      return;
    }

    // Find the participant object from the event participants
    const participant = event.participants.find(p => p.user._id === participantId);
    setSelectedParticipant(participant);
    
    // Fetch the participant's cars
    try {
      setLoadingCars(true);
      const cars = await carApi.getUserCarsByUserId(participantId);
      setParticipantCars(cars);
      setSelectedCar(cars.length > 0 ? cars[0]._id : null);
    } catch (error) {
      console.error('Error fetching participant cars:', error);
      toast.error('Error fetching participant cars');
      setParticipantCars([]);
    } finally {
      setLoadingCars(false);
    }
  };

  const handleCarChange = (event) => {
    setSelectedCar(event.target.value);
  };

  const handleAssignedUserChange = (event) => {
    const userId = event.target.value;
    console.log('Selected user ID:', userId);
    
    if (!userId) {
      setAssignedUser(null);
      return;
    }
    
    // Find the user in the allUsers array
    const user = allUsers.find(u => u._id === userId);
    console.log('Found user:', user);
    
    if (user) {
      setAssignedUser(user);
    }
  };

  const handleCreateTask = async () => {
    if (!selectedParticipant || !selectedCar || !assignedUser) {
      toast.error('Please select a participant, car, and assigned user');
      return;
    }

    try {
      setCreatingTask(true);
      
      // Calculate due date based on user input
      const eventDate = new Date(event.date);
      const dueDate = addDays(eventDate, daysUntilDue);
      
      // Create task data with event title included in the task title
      const taskData = {
        title: `Checkup: ${event.title}`,
        description: 'Complete Car Checkup',
        car: selectedCar,
        assignedTo: assignedUser._id,
        priority: 'medium',
        type: 'maintenance',
        estimatedHours: 4,
        dueDate: dueDate.toISOString()
      };
      
      // Create the task
      await taskApi.create(taskData);
      
      toast.success('Check-up task created successfully');
      setTaskCreated(true);
      
      // Reset form after successful creation
      setSelectedParticipant(null);
      setParticipantCars([]);
      setSelectedCar(null);
      setAssignedUser(null);
      setDaysUntilDue(15);
    } catch (error) {
      console.error('Error creating check-up task:', error);
      toast.error('Error creating check-up task');
    } finally {
      setCreatingTask(false);
    }
  };

  const handleCarSelection = async (car) => {
    if (!attendanceStatus) return;
    
    try {
      setUpdatingStatus(true);
      
      // Update the participant with the selected car
      await eventApi.updateParticipantStatus(eventId, {
        status: attendanceStatus,
        carId: car ? car._id : null
      });
      
      setSelectedEventCar(car);
      setCarSelectOpen(false);
      toast.success(car ? t('success.carSelected') : t('success.carRemoved'));
      
      fetchEventDetails();
    } catch (error) {
      console.error('Error updating car selection:', error);
      toast.error(t('errors.updatingCar'));
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAdminCarSelection = async (car) => {
    if (!selectedParticipantForCar) return;
    
    try {
      setUpdatingParticipantCar(true);
      
      // Update the participant with the selected car
      await eventApi.updateSpecificParticipantStatus(
        eventId, 
        selectedParticipantForCar.user._id, 
        {
          status: selectedParticipantForCar.status,
          hasPaid: selectedParticipantForCar.hasPaid,
          carId: car ? car._id : null
        }
      );
      
      setAdminCarSelectOpen(false);
      toast.success(car 
        ? t('success.adminCarSelected', { user: selectedParticipantForCar.user.name }) 
        : t('success.adminCarRemoved', { user: selectedParticipantForCar.user.name })
      );
      
      fetchEventDetails();
    } catch (error) {
      console.error('Error updating participant car:', error);
      toast.error(t('errors.updatingParticipantCar'));
    } finally {
      setUpdatingParticipantCar(false);
      setSelectedParticipantForCar(null);
    }
  };

  const handleOpenAdminCarSelect = (participant) => {
    setSelectedParticipantForCar(participant);
    fetchParticipantCars(participant.user._id);
    setAdminCarSelectOpen(true);
  };

  // Add the renderTaskCreationSection function
  const renderTaskCreationSection = () => {
    if (!event || !event.participants || event.participants.length === 0) {
      return null;
    }

    return (
      <Box>
        
        <Box alignItems="center" mb={2}>
          <Button
            variant={showTaskForm ? "contained" : "contained"}
            startIcon={<BuildIcon />}
            onClick={handleToggleTaskForm}
            color={showTaskForm ? "primary" : "inherit"}
            fullWidth
          >
            {showTaskForm ? "Hide Form" : "Create Check-up Task"}
          </Button>
        </Box>
        
        {showTaskForm && (
          <Paper elevation={2} sx={{ p: 3, mt: 2 }}>
            {taskCreated ? (
              <Box display="flex" flexDirection="column" alignItems="center" py={2}>
                <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Check-up Task Created Successfully
                </Typography>
                <Typography variant="body1" color="text.secondary" align="center" mb={2}>
                  A new check-up task has been created and assigned to the selected user.
                </Typography>
                <Button 
                  variant="contained" 
                  onClick={handleToggleTaskForm}
                >
                  Close
                </Button>
              </Box>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Create a check-up task for a participant's car. The task will have an estimated duration of 4 hours.
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Car Owner (Participant)</InputLabel>
                    <Select
                      value={selectedParticipant ? selectedParticipant.user._id : ''}
                      onChange={(e) => handleParticipantChange({
                        target: { value: e.target.value },
                        participants: event.participants
                      })}
                      label="Car Owner (Participant)"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {event.participants.map((participant) => (
                        <MenuItem key={participant.user._id} value={participant.user._id}>
                          {participant.user.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth disabled={!selectedParticipant || loadingCars}>
                    <InputLabel>Select Car</InputLabel>
                    <Select
                      value={selectedCar || ''}
                      onChange={handleCarChange}
                      label="Select Car"
                    >
                      {loadingCars ? (
                        <MenuItem value="">
                          <em>Loading cars...</em>
                        </MenuItem>
                      ) : participantCars.length === 0 ? (
                        <MenuItem value="">
                          <em>No cars available</em>
                        </MenuItem>
                      ) : (
                        participantCars.map((car) => (
                          <MenuItem key={car._id} value={car._id}>
                            {car.name} {car.model ? `(${car.model})` : ''}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Assign Task To</InputLabel>
                    <Select
                      value={assignedUser ? assignedUser._id : ''}
                      onChange={handleAssignedUserChange}
                      label="Assign Task To"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {allUsers.map((u) => (
                        <MenuItem key={u._id} value={u._id}>
                          {u.name} {u.role === 'mechanic' ? '(Mechanic)' : u.role ? `(${u.role})` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {loadingUsers && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Days Until Due"
                    type="number"
                    value={daysUntilDue}
                    onChange={handleDaysUntilDueChange}
                    InputProps={{
                      inputProps: { min: 1 }
                    }}
                    helperText={`Task will be due ${daysUntilDue} days after the event (${format(addDays(new Date(event.date), daysUntilDue), 'PPP')})`}
                  />
                </Grid>
                
                {selectedParticipant && participantCars.length === 0 && !loadingCars && (
                  <Grid item xs={12}>
                    <Alert severity="warning">
                      This participant doesn't have any cars registered in the system.
                    </Alert>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleCreateTask}
                      disabled={!selectedParticipant || !selectedCar || !assignedUser || creatingTask}
                      startIcon={creatingTask ? <CircularProgress size={20} color="inherit" /> : <BuildIcon />}
                    >
                      {creatingTask ? 'Creating Task...' : 'Create Check-up Task'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Paper>
        )}
      </Box>
    );
  };

  // Fetch participant's cars
  const fetchParticipantCars = async (participantId) => {
    if (!participantId) return;
    
    try {
      setLoadingParticipantCars(true);
      const cars = await carApi.getUserCarsByUserId(participantId);
      setParticipantCarsList(cars);
    } catch (error) {
      console.error('Error fetching participant cars:', error);
      toast.error('Failed to load participant cars');
    } finally {
      setLoadingParticipantCars(false);
    }
  };

  const handleAddParticipant = async (userId) => {
    try {
      setAddingParticipant(true);
      
      await eventApi.addSpecificParticipant(eventId, {
        userId,
        status: 'attending'
      });
      
      toast.success(t('success.participantAdded'));
      setAddParticipantDialogOpen(false);
      fetchEventDetails();
    } catch (error) {
      console.error('Error adding participant:', error);
      toast.error(t('errors.addingParticipant'));
    } finally {
      setAddingParticipant(false);
    }
  };

  const handleSaveRaceResults = async () => {
    try {
      setSavingRaceResults(true);
      
      // Format the data
      const raceResults = {
        laps: participantLaps,
        eventId: event._id
      };
      
      await eventApi.updateRaceResults(eventId, raceResults);
      
      // Count how many participants had their laps updated
      const updatedParticipantsCount = Object.keys(participantLaps).length;
      
      toast.success(
        updatedParticipantsCount > 0
          ? t('success.raceResultsUpdated', { count: updatedParticipantsCount })
          : t('success.raceResultsSaved')
      );
      
      setRaceFinishedDialogOpen(false);
      fetchEventDetails();
    } catch (error) {
      console.error('Error saving race results:', error);
      toast.error(t('errors.savingRaceResults'));
    } finally {
      setSavingRaceResults(false);
    }
  };

  const handleSaveUserLaps = async () => {
    try {
      setSavingUserLaps(true);
      
      // Format the data
      const raceResults = {
        laps: {
          [user._id]: userLapCount
        },
        eventId: event._id
      };
      
      console.log('Saving user laps:', raceResults);
      
      await eventApi.updateRaceResults(eventId, raceResults);
      
      toast.success(t('success.lapsUpdated'));
      setParticipantLapDialogOpen(false);
      
      // Refresh event details to get updated lap count
      fetchEventDetails();
    } catch (error) {
      console.error('Error updating laps:', error);
      toast.error(t('errors.updatingLaps'));
    } finally {
      setSavingUserLaps(false);
    }
  };

  const handleOpenUserLapDialog = () => {
    // Find the current user's participation
    const userParticipation = event.participants.find(p => p.user._id === user._id);
    
    // Set the initial lap count to the user's current laps or 0
    setUserLapCount(userParticipation?.laps || 0);
    setParticipantLapDialogOpen(true);
  };

  // Add a new function to handle image upload success
  const handleImageUploadSuccess = (imageUrl) => {
    setEditFormData({
      ...editFormData,
      imageUrl
    });
  };

  // Add this function to handle travel costs dialog
  const handleOpenTravelCostsDialog = () => {
    setTravelCostsDialogOpen(true);
  };

  const handleCloseTravelCostsDialog = () => {
    setTravelCostsDialogOpen(false);
  };

  // Add this function to handle travel debt payment dialog
  const handleOpenPayTravelDebtDialog = (participant) => {
    setSelectedParticipantForDebt(participant);
    setPayTravelDebtDialogOpen(true);
  };

  const handleClosePayTravelDebtDialog = () => {
    setPayTravelDebtDialogOpen(false);
    setSelectedParticipantForDebt(null);
  };

  // Add this function to handle travel debt payment
  const handlePayTravelDebt = async () => {
    if (!selectedParticipantForDebt) return;
    
    try {
      setLoading(true);
      await eventApi.updateParticipantTravelDebt(
        eventId, 
        selectedParticipantForDebt.user._id, 
        { paidBack: true }
      );
      
      // Refresh event data
      await fetchEventDetails();
      toast.success(t('success.travelDebtMarkedAsPaid'));
      handleClosePayTravelDebtDialog();
    } catch (error) {
      console.error('Error updating travel debt:', error);
      toast.error(t('errors.updatingTravelDebt'));
    } finally {
      setLoading(false);
    }
  };

  // Add this function to handle travel costs setup
  const handleSaveTravelCosts = async () => {
    try {
      setLoading(true);
      
      // Debug log for travel costs data
      console.log('Saving travel costs:', {
        hasTravelCosts: editEvent.hasTravelCosts,
        travelCostPerPerson: editEvent.travelCostPerPerson,
        travelCostsPaidBy: editEvent.travelCostsPaidBy,
        travelCostsNotes: editEvent.travelCostsNotes,
      });
      
      // Update event with travel costs data
      await eventApi.update(eventId, {
        hasTravelCosts: editEvent.hasTravelCosts,
        travelCostPerPerson: editEvent.travelCostPerPerson,
        travelCostsPaidBy: editEvent.travelCostsPaidBy,
        travelCostsNotes: editEvent.travelCostsNotes,
      });
      
      // If travel costs are enabled and there's a cost per person,
      // update all participants' travel debt
      if (editEvent.hasTravelCosts && editEvent.travelCostPerPerson > 0) {
        const participantsToUpdate = event.participants
          .filter(p => p.status === 'attending' && p.user._id !== editEvent.travelCostsPaidBy);
        
        for (const participant of participantsToUpdate) {
          await eventApi.updateParticipantTravelDebt(
            eventId,
            participant.user._id,
            {
              amount: editEvent.travelCostPerPerson,
              paidBack: false,
            }
          );
        }
      }
      
      // Refresh event data
      await fetchEventDetails();
      toast.success(t('success.travelCostsUpdated'));
      handleCloseTravelCostsDialog();
    } catch (error) {
      console.error('Error updating travel costs:', error);
      toast.error(t('errors.updatingTravelCosts'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!event) {
    return (
      <Container maxWidth="lg">
        <Box mt={4}>
          <Typography variant="h5" color="error">
            {t('errors.loadingEventDetails')}
          </Typography>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate('/events')}
            sx={{ mt: 2 }}
          >
            {t('common.back')}
          </Button>
        </Box>
      </Container>
    );
  }

  const isEventPast = new Date(event.date) < new Date();
  const isCreator = user && event.createdBy && user._id === event.createdBy._id;
  const canEdit = isCreator || (user && user.isAdmin);

  return (
    <Container maxWidth="lg">
      <Box mt={4}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/events')}
          sx={{ mb: 2 }}
        >
          {t('common.back')}
        </Button>
        
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          {isEditMode ? (
            <>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" component="h1">
                  {t('eventDetail.editTitle')}
                </Typography>
                <Box>
                  <Button 
                    onClick={() => setIsEditMode(false)} 
                    sx={{ mr: 2 }}
                    disabled={saving}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleSaveEdit}
                    disabled={saving}
                  >
                    {saving ? <CircularProgress size={24} /> : t('common.save')}
                  </Button>
                </Box>
              </Box>
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    name="title"
                    label={t('createEvent.fields.title')}
                    fullWidth
                    required
                    value={editEvent.title}
                    onChange={handleEditChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="description"
                    label={t('createEvent.fields.description')}
                    fullWidth
                    required
                    multiline
                    rows={4}
                    value={editEvent.description}
                    onChange={handleEditChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="location"
                    label={t('createEvent.fields.location')}
                    fullWidth
                    required
                    value={editEvent.location}
                    onChange={handleEditChange}
                    disabled={editEvent.isRaceday && selectedRacetrack}
                    helperText={editEvent.isRaceday && selectedRacetrack ? "Location is set from the selected racetrack" : ""}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="date"
                    label={t('createEvent.fields.date')}
                    type="date"
                    fullWidth
                    required
                    value={editEvent.date ? format(editEvent.date, 'yyyy-MM-dd') : ''}
                    onChange={handleEditChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="endDate"
                    label={t('createEvent.fields.endDate')}
                    type="date"
                    fullWidth
                    value={editEvent.endDate ? format(editEvent.endDate, 'yyyy-MM-dd') : ''}
                    onChange={handleEditChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="price"
                    label={t('createEvent.fields.price')}
                    type="number"
                    fullWidth
                    value={editEvent.price}
                    onChange={handleEditChange}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="maxParticipants"
                    label={t('createEvent.fields.maxParticipants')}
                    type="number"
                    fullWidth
                    value={editEvent.maxParticipants}
                    onChange={handleEditChange}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="signupLink"
                    label={t('createEvent.fields.signupLink')}
                    fullWidth
                    value={editEvent.signupLink}
                    onChange={handleEditChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editEvent.isPublic}
                        onChange={handleEditChange}
                        name="isPublic"
                        color="primary"
                      />
                    }
                    label={t('createEvent.fields.isPublic')}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    {editEvent.isPublic 
                      ? t('eventDetail.publicEventNote') 
                      : t('eventDetail.privateEventNote')}
                  </Typography>
                </Grid>
                
                {/* Raceday section */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editEvent.isRaceday}
                        onChange={handleEditChange}
                        name="isRaceday"
                        color="primary"
                      />
                    }
                    label="Race / Track Day"
                  />
                </Grid>
                
                {editEvent.isRaceday && (
                  <Grid item xs={12}>
                    <Autocomplete
                      id="racetrack-selection"
                      options={availableRacetracks}
                      loading={loadingRacetracks}
                      value={selectedRacetrack}
                      onChange={handleRacetrackChange}
                      getOptionLabel={(option) => `${option.name}, ${option.location}, ${option.country}`}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Racetrack"
                          required={editEvent.isRaceday}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingRacetracks ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('eventDetail.eventImage')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ImageUploader
                      onUploadSuccess={handleImageUploadSuccess}
                      buttonText={t('eventDetail.uploadImage')}
                      buttonProps={{ variant: 'contained', sx: { mr: 2 } }}
                    />
                    {editEvent.imageUrl && (
                      <Typography variant="body2" color="success.main">
                        {t('eventDetail.imageUploaded')}
                      </Typography>
                    )}
                  </Box>
                  {editEvent.imageUrl && (
                    <Box sx={{ mt: 2, mb: 3, maxWidth: 300 }}>
                      <img 
                        src={getTransformedImageUrl(editEvent.imageUrl, 'thumbnail')} 
                        alt={editEvent.title || 'Event preview'} 
                        style={{ width: '100%', borderRadius: 8 }}
                      />
                    </Box>
                  )}
                </Grid>
              </Grid>
            </>
          ) : (
            <>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Typography variant="h4" component="h1" gutterBottom>
                  {event.title}
                  {!event.isPublic && (
                    <Chip 
                      icon={<LockIcon />} 
                      label={t('eventDetail.private')} 
                      size="small" 
                      color="default" 
                      sx={{ ml: 1, verticalAlign: 'middle' }} 
                    />
                  )}
                </Typography>
                
                <Box display="flex" alignItems="center">
                  {canEdit && (
                    <Box>
                      <IconButton 
                        color="primary" 
                        onClick={() => setIsEditMode(true)}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        color="error" 
                        onClick={() => setDeleteDialogOpen(true)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs 
                  value={activeTab} 
                  onChange={(e, newValue) => setActiveTab(newValue)}
                  aria-label="event details tabs"
                  variant="fullWidth"
                  sx={{
                    '& .MuiTab-root': {
                      minWidth: { xs: '33%', sm: 'auto' }
                    }
                  }}
                >
                  <Tab 
                    icon={<InfoIcon />}
                    iconPosition="start"
                    label={<Box sx={{ display: { xs: 'none', sm: 'block' } }}>{t('eventDetail.tabs.information')}</Box>}
                    value="information"
                    sx={{
                      '& .MuiTab-iconWrapper': {
                        mr: { xs: 0, sm: 1 }
                      }
                    }}
                  />
                  <Tab 
                    icon={<PeopleIcon />}
                    iconPosition="start"
                    label={<Box sx={{ display: { xs: 'none', sm: 'block' } }}>{t('eventDetail.tabs.participants')}</Box>}
                    value="participants"
                    sx={{
                      '& .MuiTab-iconWrapper': {
                        mr: { xs: 0, sm: 1 }
                      }
                    }}
                  />
                  {event.hasTravelCosts && (
                    <Tab 
                      icon={<HotelIcon />}
                      iconPosition="start"
                      label={<Box sx={{ display: { xs: 'none', sm: 'block' } }}>{t('eventDetail.tabs.travel')}</Box>}
                      value="travel"
                      sx={{
                        '& .MuiTab-iconWrapper': {
                          mr: { xs: 0, sm: 1 }
                        }
                      }}
                    />
                  )}
                </Tabs>
              </Box>

              {/* Tab Panels */}
              {activeTab === "information" && (
                <Box>
                  {/* Display event image if available */}
                  {event.imageUrl && (
                    <Box sx={{ mb: 3, maxWidth: '100%', borderRadius: 2, overflow: 'hidden' }}>
                      <img 
                        src={getTransformedImageUrl(event.imageUrl, 'gallery')} 
                        alt={event.title} 
                        style={{ width: '100%', maxHeight: 300, objectFit: 'cover' }}
                      />
                    </Box>
                  )}
                  
                  <Typography variant="body1" paragraph>
                    {event.description}
                  </Typography>
                  
                  <Grid container spacing={4}>
                    <Grid item xs={12} md={8}>
                      <Box mt={3}>
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <Box display="flex" alignItems="center" mb={2}>
                              <LocationOnIcon color="primary" sx={{ mr: 1 }} />
                              <Typography variant="body1">{event.location}</Typography>
                            </Box>
                            
                            {event.isRaceday && event.racetrack && (
                              <Box display="flex" alignItems="center" mb={2}>
                                <DirectionsCarIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="body1">
                                  <Chip 
                                    label="Race Day" 
                                    color="secondary" 
                                    size="small" 
                                    sx={{ mr: 1 }}
                                  />
                                  {event.racetrack.name} ({event.racetrack.length} km)
                                </Typography>
                              </Box>
                            )}
                            
                            <Box display="flex" alignItems="center" mb={2}>
                              <CalendarTodayIcon color="primary" sx={{ mr: 1 }} />
                              <Typography variant="body1">
                                {format(new Date(event.date), 'PPP')}
                                {event.endDate && ` - ${format(new Date(event.endDate), 'PPP')}`}
                              </Typography>
                            </Box>
                          </Grid>
                          
                          <Grid item xs={12} sm={6}>
                            <Box display="flex" alignItems="center" mb={2}>
                              <PeopleIcon color="primary" sx={{ mr: 1 }} />
                              <Typography variant="body1">
                                {event.participants?.length || 0} 
                                {event.maxParticipants ? ` / ${event.maxParticipants}` : ''} {t('eventDetail.participants')}
                              </Typography>
                            </Box>
                            
                            {event.price > 0 && (
                              <Box display="flex" alignItems="center" mb={2}>
                                <EuroIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="body1">{event.price} €</Typography>
                              </Box>
                            )}
                            
                            {event.signupLink && (
                              <Box display="flex" alignItems="center" mb={2}>
                                <LinkIcon color="primary" sx={{ mr: 1 }} />
                                <Button
                                  variant="contained"
                                  color="primary"
                                  href={event.signupLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {t('eventDetail.signupLink')}
                                </Button>
                              </Box>
                            )}
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                          {t('attendance.yourStatus')}
                        </Typography>
                        
                        {!isEventPast ? (
                          <>
                            <ToggleButtonGroup
                              value={attendanceStatus}
                              exclusive
                              onChange={handleAttendanceChange}
                              disabled={updatingStatus || (!event.isPublic && !invitedUsers.some(u => u._id === user._id) && !canEdit)}
                              fullWidth
                              sx={{ mb: 2 }}
                            >
                              <ToggleButton value="attending" color="success">
                                {t('attendance.attending')}
                              </ToggleButton>
                              <ToggleButton value="maybe" color="warning">
                                {t('attendance.maybe')}
                              </ToggleButton>
                              <ToggleButton value="not_attending" color="error">
                                {t('attendance.notAttending')}
                              </ToggleButton>
                            </ToggleButtonGroup>
                            {updatingStatus && (
                              <Box display="flex" justifyContent="center" my={1}>
                                <CircularProgress size={24} />
                              </Box>
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {attendanceStatus ? 
                              t(`attendance.${attendanceStatus}`) : 
                              t('eventDetail.pastEvent')}
                          </Typography>
                        )}
                      </Paper>
                      
                      {attendanceStatus && event.price > 0 && (
                        <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
                          <Typography variant="h6" gutterBottom>
                            {t('eventDetail.paymentStatus')}
                          </Typography>
                          
                          <FormControlLabel
                            control={
                              <Switch
                                checked={hasPaid}
                                onChange={handlePaymentStatusChange}
                                disabled={updatingStatus}
                                color="primary"
                              />
                            }
                            label={hasPaid ? t('attendance.paid') : t('attendance.notPaid')}
                          />
                        </Paper>
                      )}
                      
                      {/* Car Selection Section */}
                      {attendanceStatus === 'attending' && (
                        <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
                          <Typography variant="h6" gutterBottom>
                            {t('eventDetail.carSelection')}
                          </Typography>
                          
                          {selectedEventCar ? (
                            <Box>
                              <Box display="flex" alignItems="center" mb={1}>
                                <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                                  <DirectionsCarIcon />
                                </Avatar>
                                <Box>
                                  <Typography variant="body1">
                                    {selectedEventCar.name}
                                  </Typography>
                                  {selectedEventCar.model && (
                                    <Typography variant="body2" color="text.secondary">
                                      {selectedEventCar.model} {selectedEventCar.year || ''}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                              <Button 
                                variant="contained" 
                                onClick={() => setCarSelectOpen(true)}
                                disabled={updatingStatus}
                                fullWidth
                              >
                                {t('eventDetail.changeCar')}
                              </Button>
                            </Box>
                          ) : (
                            <Box>
                              <Typography variant="body2" color="text.secondary" paragraph>
                                {t('eventDetail.noCarSelected')}
                              </Typography>
                              <Button 
                                variant="contained" 
                                color="primary" 
                                onClick={() => setCarSelectOpen(true)}
                                disabled={updatingStatus}
                                fullWidth
                                startIcon={<DirectionsCarIcon />}
                              >
                                {t('eventDetail.selectCar')}
                              </Button>
                            </Box>
                          )}
                        </Paper>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              )}

              {activeTab === "participants" && (
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box>
                      {!event.isPublic && canEdit && (
                        <Button
                          variant="contained" 
                          startIcon={<PersonAddIcon />}
                          onClick={() => setInviteDialogOpen(true)}
                          sx={{ mr: 1 }}
                        >
                          {t('eventDetail.inviteUsers')}
                        </Button>
                      )}
                      {user && user.isAdmin && (
                        <Button
                          variant="contained" 
                          color="secondary"
                          startIcon={<GroupAddIcon />}
                          onClick={() => setAddParticipantDialogOpen(true)}
                        >
                          {t('eventDetail.addParticipant')}
                        </Button>
                      )}
                    </Box>
                  </Box>

                  {/* Invited Users Section for Private Events */}
                  {!event.isPublic && invitedUsers.length > 0 && (
                    <Box mb={3}>
                      <Typography variant="h6" gutterBottom>
                        {t('eventDetail.invitedUsers')}
                      </Typography>
                      <Paper 
                        elevation={2} 
                        sx={{ 
                          p: 2,
                          borderRadius: 2,
                          border: '1px dashed',
                          borderColor: 'divider'
                        }}
                      >
                        <Grid container spacing={2}>
                          {invitedUsers.map(invitedUser => (
                            <Grid item key={invitedUser._id}>
                              <Tooltip title={invitedUser.name || invitedUser.username}>
                                <Box 
                                  sx={{ 
                                    textAlign: 'center',
                                    transition: 'transform 0.2s ease-in-out',
                                    '&:hover': {
                                      transform: 'scale(1.1)'
                                    }
                                  }}
                                >
                                  <UserAvatar user={invitedUser} size="medium" />
                                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                    {(invitedUser.name || invitedUser.username).split(' ')[0]}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            </Grid>
                          ))}
                        </Grid>
                        {canEdit && (
                          <Box mt={2} display="flex" justifyContent="flex-end">
                            <Button
                              size="small"
                              startIcon={<PersonAddIcon />}
                              onClick={() => setInviteDialogOpen(true)}
                            >
                              {t('eventDetail.inviteUsers')}
                            </Button>
                          </Box>
                        )}
                      </Paper>
                    </Box>
                  )}

                  {/* Participants List */}
                  {event.participants && event.participants.length > 0 ? (
                    <List>
                      <Grid container spacing={2}>
                        {event.participants
                          .sort((a, b) => {
                            // Current user always first
                            if (a.user._id === user._id) return -1;
                            if (b.user._id === user._id) return 1;
                            // Then sort by status: attending > maybe > not_attending
                            const statusOrder = { attending: 0, maybe: 1, not_attending: 2 };
                            return statusOrder[a.status] - statusOrder[b.status];
                          })
                          .map((participant) => {
                            const isCurrentUser = participant.user._id === user._id;
                            return (
                              <Grid 
                                item 
                                xs={12} 
                                sm={12} 
                                md={isCurrentUser ? 12 : 6} 
                                key={participant.user._id}
                              >
                                <Paper 
                                  elevation={isCurrentUser ? 2 : 1} 
                                  sx={{ 
                                    position: 'relative',
                                    overflow: 'hidden',
                                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                    '&:hover': {
                                      transform: 'translateY(-4px)',
                                      boxShadow: 4
                                    },
                                    ...(isCurrentUser && {
                                      border: '1px solid',
                                      borderColor: 'primary.main',
                                      background: (theme) => `linear-gradient(to right bottom, ${theme.palette.background.paper}, ${theme.palette.primary.light}05)`,
                                      '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '4px',
                                        height: '100%',
                                        backgroundColor: 'primary.main'
                                      }
                                    })
                                  }}
                                >
                                  {/* Background image if car has image */}
                                  {participant.car?.imageUrl && (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundImage: `url(${participant.car.imageUrl})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        opacity: isCurrentUser ? 0.05 : 0.1,
                                        filter: 'blur(4px)'
                                      }}
                                    />
                                  )}

                                  {/* Content */}
                                  <Box sx={{ position: 'relative', p: 2, pl: isCurrentUser ? 3 : 2 }}>
                                    {/* Header with user info and status */}
                                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
                                      <Box display="flex" alignItems="center">
                                        <UserAvatar user={participant.user} size="large" />
                                        <Box ml={2}>
                                          <Typography variant="h6" fontWeight="bold" color="text.primary">
                                            {participant.user.name}
                                            {isCurrentUser && (
                                              <Chip 
                                                label={t('eventDetail.you')}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                sx={{ ml: 1, height: 20 }}
                                              />
                                            )}
                                          </Typography>
                                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                            <Chip 
                                              label={t(`attendance.${participant.status}`)} 
                                              size="small" 
                                              color={getStatusColor(participant.status)}
                                            />
                                            {event.price > 0 && (
                                              <Chip 
                                                label={participant.hasPaid ? t('attendance.paid') : t('attendance.notPaid')} 
                                                size="small" 
                                                color={participant.hasPaid ? 'success' : 'default'}
                                                variant={participant.hasPaid ? 'filled' : 'outlined'}
                                              />
                                            )}
                                          </Box>
                                        </Box>
                                      </Box>
                                    </Box>

                                    {/* Car Information */}
                                    {participant.car ? (
                                      <Box 
                                        sx={{ 
                                          mt: 2, 
                                          p: 2, 
                                          bgcolor: 'background.paper', 
                                          borderRadius: 2,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                          boxShadow: 1
                                        }}
                                      >
                                        <Box display="flex" alignItems="center" gap={2}>
                                          {participant.car.imageUrl ? (
                                            <Box
                                              sx={{
                                                width: 80,
                                                height: 60,
                                                borderRadius: 1,
                                                overflow: 'hidden',
                                                flexShrink: 0
                                              }}
                                            >
                                              <img
                                                src={participant.car.imageUrl}
                                                alt={participant.car.name}
                                                style={{
                                                  width: '100%',
                                                  height: '100%',
                                                  objectFit: 'cover'
                                                }}
                                              />
                                            </Box>
                                          ) : (
                                            <Avatar 
                                              sx={{ 
                                                bgcolor: 'primary.main',
                                                width: 60,
                                                height: 60
                                              }}
                                            >
                                              <DirectionsCarIcon sx={{ fontSize: 30 }} />
                                            </Avatar>
                                          )}
                                          <Box>
                                            <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                                              {participant.car.name}
                                            </Typography>
                                            {participant.car.model && (
                                              <Typography variant="body2" color="text.secondary">
                                                {participant.car.model} {participant.car.year || ''}
                                              </Typography>
                                            )}
                                          </Box>
                                        </Box>
                                        
                                        {/* Admin Controls */}
                                        {(user.isAdmin || canEdit) && (
                                          <Tooltip title={t('eventDetail.editCar')}>
                                            <IconButton 
                                              size="small" 
                                              color="primary"
                                              onClick={() => handleOpenAdminCarSelect(participant)}
                                            >
                                              <EditIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </Box>
                                    ) : (
                                      <Box 
                                        sx={{ 
                                          mt: 2, 
                                          p: 2, 
                                          bgcolor: 'background.paper',
                                          borderRadius: 2,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                          boxShadow: 1
                                        }}
                                      >
                                        <Box display="flex" alignItems="center">
                                          <Avatar 
                                            sx={{ 
                                              bgcolor: 'grey.300',
                                              width: 60,
                                              height: 60,
                                              mr: 2
                                            }}
                                          >
                                            <ClearIcon sx={{ fontSize: 30 }} />
                                          </Avatar>
                                          <Typography variant="body1" color="text.secondary">
                                            {t('eventDetail.noCar')}
                                          </Typography>
                                        </Box>
                                        
                                        {(user.isAdmin || canEdit) && (
                                          <IconButton 
                                            size="small" 
                                            color="primary"
                                            onClick={() => handleOpenAdminCarSelect(participant)}
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        )}
                                      </Box>
                                    )}

                                    {/* Travel Debt Information */}
                                    {event.hasTravelCosts && event.travelCostsPaidBy && (
                                      participant.user._id !== event.travelCostsPaidBy._id ? (
                                        <Box 
                                          sx={{ 
                                            mt: 2, 
                                            p: 2, 
                                            bgcolor: 'background.paper',
                                            borderRadius: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            boxShadow: 1,
                                            border: '1px solid',
                                            borderColor: participant.travelDebt?.paidBack ? 'success.light' : 'warning.light'
                                          }}
                                        >
                                          <Box display="flex" alignItems="center">
                                            <Avatar 
                                              sx={{ 
                                                bgcolor: participant.travelDebt?.paidBack ? 'success.main' : 'warning.main',
                                                width: 40,
                                                height: 40,
                                                mr: 2
                                              }}
                                            >
                                              <MoneyIcon />
                                            </Avatar>
                                            <Box>
                                              <Typography variant="subtitle2" color="text.primary">
                                                {t('eventDetail.travelCost')}: {participant.travelDebt?.amount || event.travelCostPerPerson}€
                                              </Typography>
                                              <Typography 
                                                variant="body2" 
                                                color={participant.travelDebt?.paidBack ? "success.main" : "warning.main"}
                                              >
                                                {participant.travelDebt?.paidBack ? t('eventDetail.paid') : t('eventDetail.unpaid')}
                                              </Typography>
                                            </Box>
                                          </Box>
                                          
                                          {!participant.travelDebt?.paidBack && 
                                           (user.isAdmin || user._id === event.travelCostsPaidBy._id || user._id === participant.user._id) && (
                                            <Button
                                              size="small"
                                              variant="contained"
                                              color="success"
                                              onClick={() => handleOpenPayTravelDebtDialog(participant)}
                                            >
                                              {t('eventDetail.markAsPaid')}
                                            </Button>
                                          )}
                                        </Box>
                                      ) : (
                                        <Box 
                                          sx={{ 
                                            mt: 2, 
                                            p: 2, 
                                            bgcolor: 'background.paper',
                                            borderRadius: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            boxShadow: 1,
                                            border: '1px solid',
                                            borderColor: 'info.light'
                                          }}
                                        >
                                          <Box display="flex" alignItems="center">
                                            <Avatar 
                                              sx={{ 
                                                bgcolor: 'info.main',
                                                width: 40,
                                                height: 40,
                                                mr: 2
                                              }}
                                            >
                                              <ReceiptIcon />
                                            </Avatar>
                                            <Box>
                                              <Typography variant="subtitle2" color="text.primary">
                                                {participant.user._id === user._id ? 
                                                  t('eventDetail.youPaidForEveryone') : 
                                                  t('eventDetail.userPaidForEveryone', { name: participant.user.name })}
                                              </Typography>
                                              <Typography variant="body2" color="info.main">
                                                {t('eventDetail.total')}: {(event.travelCostPerPerson * event.participants.filter(p => 
                                                  p.status === 'attending' && p.user._id !== event.travelCostsPaidBy._id
                                                ).length).toFixed(2)}€
                                              </Typography>
                                            </Box>
                                          </Box>
                                        </Box>
                                      )
                                    )}

                                    {/* Race Results */}
                                    {event.isRaceday && participant.car && participant.laps > 0 && (
                                      <Box 
                                        sx={{ 
                                          mt: 2,
                                          p: 2,
                                          bgcolor: 'background.paper',
                                          borderRadius: 2,
                                          boxShadow: 1
                                        }}
                                      >
                                        <Grid container spacing={2}>
                                          <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">
                                              {t('eventDetail.laps')}
                                            </Typography>
                                            <Typography variant="h6" fontWeight="bold" color="text.primary">
                                              {participant.laps}
                                            </Typography>
                                          </Grid>
                                          <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">
                                              {t('eventDetail.kilometers')}
                                            </Typography>
                                            <Typography variant="h6" fontWeight="bold" color="text.primary">
                                              {participant.kilometers.toFixed(0)} km
                                            </Typography>
                                          </Grid>
                                        </Grid>
                                      </Box>
                                    )}

                                    {/* Actions */}
                                    <Box 
                                      display="flex" 
                                      justifyContent="space-between" 
                                      alignItems="center" 
                                      mt={2}
                                      sx={{
                                        opacity: 0.8,
                                        transition: 'opacity 0.2s',
                                        '&:hover': { opacity: 1 }
                                      }}
                                    >
                                      {((canEdit || user.isAdmin) && participant.user._id !== user._id) || (participant.user._id === user._id) ? (
                                        <Button
                                          size="small"
                                          color="error"
                                          startIcon={<PersonRemoveIcon />}
                                          onClick={() => handleRemoveParticipant(participant.user._id)}
                                          variant="outlined"
                                        >
                                          {participant.user._id === user._id ? t('eventDetail.leaveEvent') : t('eventDetail.removeParticipant')}
                                        </Button>
                                      ) : <Box />}
                                      
                                      {participant.user._id === user._id && participant.status === 'attending' && (
                                        <Button
                                          variant="contained" 
                                          size="small"
                                          color="primary"
                                          startIcon={<EditIcon />}
                                          onClick={handleOpenUserLapDialog}
                                        >
                                          {t('eventDetail.updateLaps')}
                                        </Button>
                                      )}
                                    </Box>
                                  </Box>
                                </Paper>
                              </Grid>
                            );
                        })}
                      </Grid>
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t('eventDetail.noParticipants')}
                    </Typography>
                  )}
                </Box>
              )}

              {activeTab === "travel" && event.hasTravelCosts && (
                <Box>
                  <Paper elevation={2} sx={{ p: 3 }}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <HotelIcon color="primary" sx={{ mr: 2, fontSize: 28 }} />
                      <Typography variant="h6">{t('eventDetail.travelCosts')}</Typography>
                    </Box>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">{t('eventDetail.costPerPerson')}:</Typography>
                        <Typography variant="body1" fontWeight="bold">{event.travelCostPerPerson}€</Typography>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">{t('eventDetail.paidBy')}:</Typography>
                        <Box display="flex" alignItems="center">
                          {event.travelCostsPaidBy ? (
                            <>
                              <UserAvatar 
                                user={event.travelCostsPaidBy} 
                                size="small" 
                              />
                              <Typography variant="body1" fontWeight="bold" sx={{ ml: 1 }}>
                                {event.travelCostsPaidBy.name || event.travelCostsPaidBy.username || t('common.unknown')}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body1" color="text.secondary">{t('eventDetail.notSpecified')}</Typography>
                          )}
                        </Box>
                      </Grid>
                      
                      {event.travelCostsNotes && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="text.secondary">{t('eventDetail.notes')}:</Typography>
                          <Typography variant="body1">{event.travelCostsNotes}</Typography>
                        </Grid>
                      )}
                    </Grid>
                    
                    {canEdit && (
                      <Box display="flex" justifyContent="flex-end" mt={2}>
                        <Button 
                          variant="outlined" 
                          startIcon={<EditIcon />}
                          onClick={handleOpenTravelCostsDialog}
                        >
                          {t('eventDetail.editTravelCosts')}
                        </Button>
                      </Box>
                    )}
                  </Paper>
                </Box>
              )}
            </>
          )}
          
        </Paper>
      </Box>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t('common.delete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('eventDetail.confirmDelete')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDeleteEvent} color="error" autoFocus>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite Users Dialog */}
      <Dialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <PersonAddIcon sx={{ mr: 1 }} />
            {t('eventDetail.inviteUsers')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box mb={3}>
            <Typography variant="body2" color="text.secondary" paragraph>
              {event && !event.isPublic 
                ? t('eventDetail.inviteUsersDescription')
                : t('createEvent.participantsNote')}
            </Typography>
            <Autocomplete
              options={availableUsers}
              getOptionLabel={(option) => option.name || option.username}
              loading={loadingUsers}
              value={selectedUser}
              onChange={(event, newValue) => {
                setSelectedUser(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('eventDetail.selectUser')}
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box display="flex" alignItems="center">
                    <UserAvatar user={option} size="small" />
                    <Box sx={{ ml: 1 }}>
                      <Typography variant="body1">{option.name || option.username}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.email}
                      </Typography>
                      {option.role && (
                        <Chip 
                          label={option.role}
                          size="small"
                          color={option.isAdmin ? "error" : "primary"}
                          sx={{ mt: 0.5, height: 20 }}
                        />
                      )}
                    </Box>
                  </Box>
                </li>
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>
            {t('common.close')}
          </Button>
          <Button 
            onClick={handleInviteUser} 
            variant="contained" 
            color="primary"
            disabled={!selectedUser || inviting}
          >
            {inviting ? <CircularProgress size={24} /> : t('eventDetail.invite')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Car Selection Dialog */}
      <Dialog open={carSelectOpen} onClose={() => setCarSelectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('eventDetail.selectCar')}</DialogTitle>
        <DialogContent>
          {loadingUserCars ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : userCars.length > 0 ? (
            <List>
              {userCars.map(car => (
                <ListItem key={car._id} disablePadding>
                  <ListItemButton 
                    onClick={() => handleCarSelection(car)}
                    selected={selectedEventCar && selectedEventCar._id === car._id}
                  >
                    <ListItemAvatar>
                      <Avatar>
                        <DirectionsCarIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary={car.name} 
                      secondary={car.model ? `${car.model} ${car.year || ''}` : ''}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
              <Divider sx={{ my: 2 }} />
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => handleCarSelection(null)}
                  selected={!selectedEventCar}
                >
                  <ListItemAvatar>
                    <Avatar>
                      <ClearIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary={t('eventDetail.noCar')}
                    secondary={t('eventDetail.noCarDescription')}
                  />
                </ListItemButton>
              </ListItem>
            </List>
          ) : (
            <Box textAlign="center" py={3}>
              <Typography variant="body1" gutterBottom>
                {t('eventDetail.noCarsAvailable')}
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => navigate('/cars/new')}
                sx={{ mt: 2 }}
              >
                {t('eventDetail.addCar')}
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCarSelectOpen(false)}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Admin Car Selection Dialog */}
      <Dialog 
        open={adminCarSelectOpen} 
        onClose={() => setAdminCarSelectOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {t('eventDetail.adminSelectCar', { 
            user: selectedParticipantForCar?.user?.name || t('common.unknown') 
          })}
        </DialogTitle>
        <DialogContent>
          {loadingParticipantCars ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : participantCarsList.length > 0 ? (
            <List>
              {participantCarsList.map(car => (
                <ListItem key={car._id} disablePadding>
                  <ListItemButton 
                    onClick={() => handleAdminCarSelection(car)}
                    selected={selectedParticipantForCar?.car?._id === car._id}
                  >
                    <ListItemAvatar>
                      <Avatar>
                        <DirectionsCarIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary={car.name} 
                      secondary={car.model ? `${car.model} ${car.year || ''}` : ''}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
              <Divider sx={{ my: 2 }} />
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => handleAdminCarSelection(null)}
                  selected={!selectedParticipantForCar?.car}
                >
                  <ListItemAvatar>
                    <Avatar>
                      <ClearIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary={t('eventDetail.noCar')}
                    secondary={t('eventDetail.noCarDescription')}
                  />
                </ListItemButton>
              </ListItem>
            </List>
          ) : (
            <Box textAlign="center" py={3}>
              <Typography variant="body1" gutterBottom>
                {t('eventDetail.participantNoCarsAvailable')}
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => {
                  setAdminCarSelectOpen(false);
                  navigate('/cars/new');
                }}
                sx={{ mt: 2 }}
              >
                {t('eventDetail.addCarForUser')}
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setAdminCarSelectOpen(false)}
            disabled={updatingParticipantCar}
          >
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Participant Dialog */}
      <Dialog
        open={addParticipantDialogOpen}
        onClose={() => setAddParticipantDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <GroupAddIcon sx={{ mr: 1 }} />
            {t('eventDetail.addParticipant')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('eventDetail.addParticipantDescription')}
          </DialogContentText>
          
          {loadingUsers ? (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {allUsers
                .filter(u => !event.participants.some(p => p.user._id === u._id))
                .map(user => (
                  <ListItem key={user._id} disablePadding>
                    <ListItemButton 
                      onClick={() => handleAddParticipant(user._id)}
                      disabled={addingParticipant}
                      sx={{
                        borderRadius: 1,
                        mb: 1,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        }
                      }}
                    >
                      <ListItemAvatar>
                        <UserAvatar user={user} />
                      </ListItemAvatar>
                      <ListItemText 
                        primary={
                          <Box display="flex" alignItems="center">
                            <Typography variant="body1">{user.name || user.username}</Typography>
                            {user.role && (
                              <Chip 
                                label={user.role}
                                size="small"
                                color={user.isAdmin ? "error" : "primary"}
                                sx={{ ml: 1, height: 20 }}
                              />
                            )}
                          </Box>
                        }
                        secondary={user.email}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
            </List>
          )}
          
          {allUsers.filter(u => !event.participants.some(p => p.user._id === u._id)).length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('eventDetail.noUsersToAdd')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddParticipantDialogOpen(false)}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Race Finished Dialog */}
      <Dialog
        open={raceFinishedDialogOpen}
        onClose={() => !savingRaceResults && setRaceFinishedDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <FlagIcon sx={{ mr: 1 }} />
            {t('eventDetail.raceResults')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <DialogContentText>
              {t('eventDetail.raceFinishedDescription')}
            </DialogContentText>
            {isEventPast && (
              <Chip 
                label={t('eventList.past')} 
                color="default" 
                size="small" 
                sx={{ ml: 2 }}
              />
            )}
          </Box>
          
          {event && event.participants && event.participants.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('eventDetail.participant')}</TableCell>
                    <TableCell>{t('eventDetail.car')}</TableCell>
                    <TableCell align="right">{t('eventDetail.laps')}</TableCell>
                    <TableCell align="right">{t('eventDetail.kilometers')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {event.participants
                    .filter(p => p.status === 'attending' && p.car)
                    .map((participant) => {
                      const trackLength = event.racetrack ? event.racetrack.length : 0;
                      const laps = participantLaps[participant.user._id] || participant.laps || 0;
                      const kilometers = (laps * trackLength).toFixed(2);
                      
                      return (
                        <TableRow key={participant.user._id}>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <UserAvatar user={participant.user} size="small" />
                              <Typography sx={{ ml: 1 }}>
                                {participant.user.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {participant.car ? (
                              <Typography>
                                {participant.car.name} {participant.car.model ? `(${participant.car.model})` : ''}
                              </Typography>
                            ) : (
                              <Typography color="text.secondary">
                                {t('eventDetail.noCar')}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number"
                              value={laps}
                              onChange={(e) => {
                                const value = parseInt(e.target.value) || 0;
                                setParticipantLaps({
                                  ...participantLaps,
                                  [participant.user._id]: value
                                });
                              }}
                              inputProps={{ min: 0, step: 1 }}
                              size="small"
                              sx={{ width: 80 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {kilometers} km
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">
              {t('eventDetail.noParticipantsWithCars')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setRaceFinishedDialogOpen(false)} 
            disabled={savingRaceResults}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleSaveRaceResults} 
            variant="contained" 
            color="primary"
            disabled={savingRaceResults}
            startIcon={savingRaceResults ? <CircularProgress size={24} /> : null}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Participant Lap Update Dialog */}
      <Dialog
        open={participantLapDialogOpen}
        onClose={() => !savingUserLaps && setParticipantLapDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <FlagIcon sx={{ mr: 1 }} />
            {t('eventDetail.updateYourLaps')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box my={2}>
            <DialogContentText>
              {t('eventDetail.updateYourLapsDescription')}
            </DialogContentText>
            
            <Box mt={3}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6}>
                  <TextField
                    label={t('eventDetail.laps')}
                    type="number"
                    value={userLapCount}
                    onChange={(e) => setUserLapCount(parseInt(e.target.value) || 0)}
                    inputProps={{ min: 0, step: 1 }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1">
                    {t('eventDetail.kilometers')}: {((userLapCount || 0) * (event?.racetrack?.length || 0)).toFixed(2)} km
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setParticipantLapDialogOpen(false)} 
            disabled={savingUserLaps}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleSaveUserLaps} 
            variant="contained" 
            color="primary"
            disabled={savingUserLaps}
          >
            {savingUserLaps ? (
              <CircularProgress size={24} />
            ) : (
              t('common.save')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Travel Costs Dialog */}
      <Dialog open={travelCostsDialogOpen} onClose={handleCloseTravelCostsDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editEvent.hasTravelCosts ? t('eventDetail.editTravelCosts') : t('eventDetail.addTravelCosts')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={editEvent.hasTravelCosts}
                  onChange={handleEditChange}
                  name="hasTravelCosts"
                  color="primary"
                />
              }
              label={t('eventDetail.hasTravelCosts')}
            />
            
            {editEvent.hasTravelCosts && (
              <>
                <TextField
                  fullWidth
                  label={t('eventDetail.costPerPerson')}
                  type="number"
                  name="travelCostPerPerson"
                  value={editEvent.travelCostPerPerson}
                  onChange={handleEditChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  }}
                  sx={{ mt: 2 }}
                />
                
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel id="travel-costs-paid-by-label">{t('eventDetail.paidBy')}</InputLabel>
                  <Select
                    labelId="travel-costs-paid-by-label"
                    name="travelCostsPaidBy"
                    value={editEvent.travelCostsPaidBy}
                    onChange={handleEditChange}
                    label={t('eventDetail.paidBy')}
                  >
                    <MenuItem value="">
                      <em>{t('eventDetail.notSpecified')}</em>
                    </MenuItem>
                    {event.participants
                      .filter(p => p.status === 'attending')
                      .map(participant => (
                        <MenuItem key={participant.user._id} value={participant.user._id}>
                          {participant.user.name || participant.user.username}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                
                <TextField
                  fullWidth
                  label={t('eventDetail.notes')}
                  name="travelCostsNotes"
                  value={editEvent.travelCostsNotes}
                  onChange={handleEditChange}
                  multiline
                  rows={3}
                  sx={{ mt: 2 }}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTravelCostsDialog}>{t('common.cancel')}</Button>
          <Button 
            onClick={handleSaveTravelCosts} 
            variant="contained" 
            disabled={editEvent.hasTravelCosts && (!editEvent.travelCostPerPerson || !editEvent.travelCostsPaidBy)}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pay Travel Debt Dialog */}
      <Dialog open={payTravelDebtDialogOpen} onClose={handleClosePayTravelDebtDialog}>
        <DialogTitle>{t('eventDetail.confirmPayTravelDebt')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {t('eventDetail.confirmPayTravelDebtDescription', {
              username: selectedParticipantForDebt?.user.username,
              amount: selectedParticipantForDebt?.travelDebt?.amount || 0
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePayTravelDebtDialog}>{t('common.cancel')}</Button>
          <Button onClick={handlePayTravelDebt} variant="contained" color="primary">
            {t('eventDetail.markAsPaid')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EventDetailPage; 